/**
 * §14b — moving a hearing: the copy, and nothing that a server sentence already says.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THERE IS NO COPY OF A REFUSAL IN THIS FILE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The server refuses four things — the hearing has been held, the case was closed without one,
 * no reason, and an amendment that moves nothing — and each refusal arrives with the sentence
 * migration 068 raises and the fixtures twin mirrors. Those sentences are printed verbatim,
 * exactly as §9's gate message and §12's service refusals are.
 *
 * Restating two of them here so the form could refuse a fraction of a second sooner was
 * considered and rejected: a duplicated sentence drifts, and the copy that drifts is the one on
 * the screen. Instead the form makes the two refusable states UNREACHABLE — `Save Amendment`
 * is disabled until something has actually moved and a reason has been typed, with a hint
 * saying which is missing, which is `ServiceCapture`'s own idiom for a control that cannot yet
 * do its job ("a real disabled button plus a hint — deliberately not an absent control").
 *
 * What a typed reason may CONTAIN is a different matter, and that rule does live in one place
 * already: `recordTextFault` and `recordTextRefusal` in `@hockeytd/shared`, which the route
 * applies a moment later. The form calls the same two functions, so the sentence the Technical
 * Delegate reads is the route's own either way.
 *
 * British English throughout.
 */

import type { HearingAmendmentLetterAction, HearingAmendmentView } from "../../api/types.js";
import { formatInstant } from "./caseText.js";

export const AMEND_TITLE = "Amend Hearing Details";

export const AMEND_NOTE = "The Time And The Place Of A Hearing That Has Not Yet Been Held";

/**
 * What this form is for, and — just as importantly — what it is not.
 *
 * The Technical Delegate opening it has a case in front of him with a charge on it, and the
 * first thing he needs to know is that this is not where a charge is changed. §14b's reasoning
 * is that amending a charge changes what the person must answer, which requires a fresh notice
 * rather than an amended record; the form says the boundary rather than leaving him to discover
 * it by looking for a field that is not there.
 */
export const AMEND_WHAT =
  "Move the hearing to a new time, a new place, or both. The match, the persons involved, the " +
  "charges and the account of the incident are the record of the charge and are not changed " +
  "here — a different charge is a different case to answer, and it needs a fresh notice.";

export const AMEND_REASON_LABEL = "Why The Hearing Is Being Moved";

/**
 * §12a's warning about a reason, in §12a's own terms, because this reason is kept the same way.
 *
 * It is written above the box rather than under it: a caution about what not to type is worth
 * nothing after it has been typed.
 */
export const AMEND_REASON_HINT =
  "Kept with the case record, cannot be edited or removed, and shown on an appeal — so leave " +
  "out health details and anything else personal. One line is enough: what changed, and why.";

export const AMEND_TIME_HINT =
  "Leave both boxes empty if the hearing is postponed and a new time is still to be fixed.";

export const AMEND_PLACE_HINT =
  "Where the parties are to come. The Hearing Letter prints this exactly as it is typed.";

export const AMEND_SAVE = "Save Amendment";
export const AMEND_CANCEL = "Cancel";

/**
 * What is still missing before an amendment can be saved, or null when nothing is.
 *
 * The two conditions the server would otherwise refuse, said as what to DO rather than as what
 * is wrong — the form's job is to get the Technical Delegate to a saveable amendment, and
 * "moves nothing" is a diagnosis where "change the time or the place" is an instruction.
 */
export function amendBlocker(options: {
  moved: boolean;
  hasReason: boolean;
}): string | null {
  if (!options.moved && !options.hasReason) {
    return "Change the time or the place, and say why, to save an amendment.";
  }
  if (!options.moved) {
    return "Nothing has moved yet. Change the time or the place of the hearing.";
  }
  if (!options.hasReason) {
    return "Say why the hearing is being moved. The reason is kept with the case record.";
  }
  return null;
}

/**
 * Why the control is disabled, or null when it is not — §9's gate-message idiom.
 *
 * The server's own refusals are fuller and name the reopen; these are the GATE, in the shape
 * §9's card table uses for a batch that is not yet available (*"Available once the Hearing
 * Letter has been issued."*). Written as availability rather than as refusal on purpose: this
 * is read by a Technical Delegate who has not pressed anything, and it is not the same sentence
 * as the one he would meet if he had.
 *
 * The condition is the minutes' state, which is migration 030's definition of when a hearing
 * record is editable at all, and is exactly what the server tests.
 */
export function amendDisabledReason(minutesState: string): string | null {
  if (minutesState === "pending" || minutesState === "draft") return null;
  if (minutesState === "not_required") {
    return "Available while the hearing is still to come. This case was closed without one.";
  }
  return (
    "Available while the hearing is still to come. This hearing has been held and its minutes " +
    "are filed — reopen them in the Documents panel if the record of it is wrong."
  );
}

/**
 * WHAT THE AMENDMENT WILL DO TO THE HEARING LETTER, said BEFORE it is saved.
 *
 * The server says what it DID, in `letterNote`, and that sentence is printed verbatim
 * afterwards. This one is the forecast, and it exists because the Technical Delegate's whole
 * complaint was about the letter: he needs to know, at the moment he presses Save, whether the
 * document that has already gone out will still say the old thing.
 *
 * It is deliberately NOT the same sentence read twice. A forecast that turned out to be wrong —
 * a generation the server refused, a letter another Technical Delegate issued in the meantime —
 * would be corrected by the receipt, and a screen that showed the forecast as though it were
 * the outcome could not be.
 */
export function amendLetterForecast(
  letterState: string,
  letterServedAt: string | null,
): { kind: "advisory" | "attention"; text: string } {
  if (letterState === "pending") {
    return {
      kind: "advisory",
      text:
        "The Hearing Letter has not been drafted, so nothing is out of date: the first draft " +
        "will be composed from the amended record.",
    };
  }
  if (letterState === "draft") {
    return {
      kind: "advisory",
      text:
        "The Hearing Letter is a draft. Choose below whether to compose it again from the " +
        "amended record or to keep the wording you wrote.",
    };
  }
  return {
    kind: "attention",
    text:
      "The Hearing Letter has been issued and names the old time and place. This amendment " +
      "does not touch it: reopen it for editing, re-draft it from the case, and issue it " +
      "again." +
      (letterServedAt === null
        ? ""
        : " It has already been served, so the letter the Team Manager holds is wrong — " +
          "serve the re-issued letter and record that service."),
  };
}

/** The two-way choice §10d's confirmation makes into a press. Safe option first, and default. */
export const AMEND_REDRAFT_CHOICE: readonly { value: boolean; label: string }[] = [
  { value: false, label: "Keep My Wording" },
  { value: true, label: "Re-Draft The Letter" },
];

/**
 * §10d's loss, stated where the choice is made.
 *
 * §9's banner promises *"the TD's wording is the wording that goes out"*, so recomposing
 * destroys the one version of the letter he has vouched for. The Documents panel arms that
 * press behind a confirmation strip; here the press IS the choice, and it is not the default,
 * so what it costs is written beside it instead.
 */
export const AMEND_REDRAFT_WARNING =
  "Re-drafting composes the letter again from the case record and replaces the draft, so any " +
  "wording you edited by hand is gone. Keeping your wording leaves the draft naming the old " +
  "time and place until you re-draft it from the Documents panel.";

/* ────────────────────────────────────────────────────────────────────────────
 * The Amendment History
 * ──────────────────────────────────────────────────────────────────────────── */

export const AMEND_HISTORY_HEADING = "Amendment History";

/**
 * One line of it, in three parts — §12a's Service History shape, because it is the same kind of
 * record and there is no reason for the case file to say one of them in a second way.
 *
 * `—` stands for an arrangement that was not set: a hearing with no time yet, or one postponed
 * back to none. It is printed rather than hidden, because "moved from nothing to Wednesday" and
 * "moved from Tuesday to nothing" are different facts and both need to be readable.
 */
export interface AmendmentHistoryLine {
  fromAt: string;
  fromPlace: string;
  toAt: string;
  toPlace: string;
  /** True when the letter had been issued when the hearing moved — the line a reader re-reads. */
  letterWasOut: boolean;
  letterWasServed: boolean;
  reason: string;
  who: string;
  when: string;
}

const NOT_SET = "—";

export function amendmentHistoryLine(amendment: HearingAmendmentView): AmendmentHistoryLine {
  return {
    fromAt: formatInstant(amendment.fromHearingAt) ?? NOT_SET,
    fromPlace: amendment.fromHearingLocation ?? NOT_SET,
    toAt: formatInstant(amendment.toHearingAt) ?? NOT_SET,
    toPlace: amendment.toHearingLocation ?? NOT_SET,
    // `filed` cannot happen on batch 1 and `not_required` means no letter was ever wanted;
    // what matters to a reader is the one state in which a document had left the building.
    letterWasOut: amendment.letterState === "issued",
    letterWasServed: amendment.letterServedAt !== null,
    reason: amendment.reason,
    who: amendment.actorMediaName,
    when: formatInstant(amendment.at) ?? NOT_SET,
  };
}

/**
 * The note a line carries when the letter had already gone out — or nothing, when it had not.
 *
 * Kept out of the line's three parts so that the ordinary amendment, made before anybody was
 * told anything, reads as the ordinary thing it is.
 */
export function amendmentLetterNoteOnLine(line: AmendmentHistoryLine): string | null {
  if (!line.letterWasOut) return null;
  return line.letterWasServed
    ? "The Hearing Letter had been issued and served when this amendment was made."
    : "The Hearing Letter had been issued when this amendment was made.";
}

/** The kind of notice the receipt is drawn in. A letter left out of date is not good news. */
export function amendmentReceiptKind(
  action: HearingAmendmentLetterAction,
): "advisory" | "attention" {
  return action === "recomposed" || action === "not_yet_drafted" ? "advisory" : "attention";
}

/** The receipt's own kicker. The sentence beside it is the server's, printed verbatim. */
export const AMEND_RECEIPT_LABEL = "Hearing Moved";
